/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionClearMessages';

const ActionClearMessagesManager: ActionManagerFn<'ActionClearMessages'> = (
  platform: Platform,
): ActionManager<'ActionClearMessages'> => {
  return {
    ...baseActionManager(platform, 'ActionClearMessages'),
    defaultValue,
    props: {
      ...baseActionProps,
      allMessages: {
        label: 'Clear all messages',
        type: 'boolean',
        helpText: 'If true, all messages will be cleared.',
      },
      info: {
        label: 'Include Info',
        type: 'boolean',
        helpText: 'If true, all info messages will be cleared.',
      },
      success: {
        label: 'Include Success',
        type: 'boolean',
        helpText: 'If true, all success messages will be cleared.',
      },
      warning: {
        label: 'Include Warnings',
        type: 'boolean',
        helpText: 'If true, all warning messages will be cleared.',
      },
      error: {
        label: 'Include Errors',
        type: 'boolean',
        helpText: 'If true, all error messages will be cleared.',
      },
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'error':
        case 'info':
        case 'success':
        case 'warning':
          return props.allMessages !== true;
        default:
          return true;
      }
    },
    name: 'Clear Messages',
    icon: { iconName: 'eraser', iconType: 'light' },
    description: `**Overview**<br/>
The Clear Messages action is a utility component designed to manage user feedback by removing specific or all notifications from the system. It functions as a cleanup mechanism, allowing builders to dismiss messages by severity (Info, Success, Warning, Error) or clear all messages at once.<br/><br/>
The component provides a dynamic property interface that prioritizes a global "Clear all" setting; when enabled, it hides the individual message-type toggles to prevent redundant configuration.

**Key Features**<br/>
* **Granular Message Filtering:** Provides boolean toggles to target individual message types: info, success, warning, and error.
* **Global Override:** Includes an allMessages property that acts as a master switch to remove every message regardless of type.
* **Dynamic Property Visibility:** Uses isPropertyVisible logic to hide the Info/Success/Warning/Error toggles whenever allMessages is true.

**Properties**<br/>
* **Clear all messages (allMessages):** Boolean.
  * *Effect:* If true, all messages are cleared.
  * *Behavior:* When checked, it hides the other "Include" properties.
* **Include Info (info):** Boolean.
  * *Effect:* If true, clears all informational messages.
  * *Visibility:* Only visible if allMessages is not true.
* **Include Success (success):** Boolean.
  * *Effect:* If true, clears all success messages.
  * *Visibility:* Only visible if allMessages is not true.
* **Include Warnings (warning):** Boolean.
  * *Effect:* If true, clears all warning messages.
  * *Visibility:* Only visible if allMessages is not true.
* **Include Errors (error):** Boolean.
  * *Effect:* If true, clears all error messages.
  * *Visibility:* Only visible if allMessages is not true.

**Use Cases**<br/>
* **State Reset:** Clearing previous success/error notifications before initiating a new form submission or data transaction.
* **Error Recovery:** Removing old error messages automatically when a user successfully corrects an input or retries a failed operation.`,
  };
};

export default ActionClearMessagesManager;
